import React, { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import type { CollabEditProvider, Provider } from '@atlaskit/collab-provider';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

export type UseCollaboratorLimitsParams = {
	contentId: string;
	spaceId?: string;
	flags: FlagsStateContainer;
	isEditLimitExceeded: boolean;
	collabEditProvider?: (CollabEditProvider & Provider) | null;
	isLiveViewLimitExceeded?: boolean;
};

const i18n = defineMessages({
	titleKeywordForAutomationDiscovery: {
		id: 'native-collab.title-keyword-for-automation-discovery.',
		defaultMessage: 'meeting',
		description: 'Keyword in title that triggers automation discovery dialog',
	},
	// Collaborative editing limits, notification flag
	collabLimitReachedTitle: {
		id: 'native-collab.collab-limit-reached-title',
		defaultMessage: 'You can’t edit this content right now',
		description: 'Notice when the collab limit is reached and the user is unable to edit the page.',
	},
	collabLimitReachedDescription: {
		id: 'native-collab.collab-limit-reached-description',
		defaultMessage:
			'You’ve reached the max number of active editors. Try again when it’s not so busy.',
		description: 'Notice when the collab limit is reached and the user is unable to edit the page.',
	},
	underCollabLimitTitle: {
		id: 'native-collab.collab.under.collab-limit-title',
		defaultMessage: 'Ready to go',
		description:
			'Notice when the editing session is under the collab limit and editing the page is available again.',
	},
	underCollabLimitDescription: {
		id: 'native-collab.collab.under.collab-limit-description',
		defaultMessage: 'We’re autosaving your changes, so you never need to press save.',
		description:
			'Notice when the editing session is under the collab limit and editing the page is available again.',
	},
	// Live pages view mode limits
	livePagesViewLimitReachedTitle: {
		id: 'native-collab.live-pages-limit-reached-title',
		defaultMessage: 'This content can’t be viewed right now. Reload and try again.',
		description:
			'Notice when the live pages view mode limit is reached and the user is unable to view the page.',
	},
	livePagesViewLimitReachedDescription: {
		id: 'native-collab.live-pages-limit-reached-description',
		defaultMessage:
			'You’ve reached the max number of active viewers. Try again when it’s not so busy.',
		description:
			'Notice when the live pages view mode limit is reached and the user is unable to view the page.',
	},
	underLivePagesViewLimitTitle: {
		id: 'native-collab.live-pages.under.live-pages-limit-title',
		defaultMessage: 'Ready to go',
		description:
			'Notice when the live pages view mode session is under the limit and viewing the page is available again.',
	},
});

// Handles UX for when the user hits edit (generic) or view (live-pages only) single-document collaboration limit
export const useCollaboratorLimits = ({
	contentId,
	spaceId,
	flags,
	isEditLimitExceeded, // flag will be shown indicating that too many participants are editing
	collabEditProvider,
	isLiveViewLimitExceeded = false, // triggers static fallback view mode where editor will show content but be disconnected from NCS
}: UseCollaboratorLimitsParams) => {
	// To prevent the success message from showing on mount, we can use a ref to store if the user was previously at the collab limit or not
	const hasShownEditLimitFlag = useRef<boolean>(false);
	const hasShownLiveViewLimitFlag = useRef<boolean>(false);

	const isLivePage = useIsCurrentPageLive();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendLimitReachedEvent = useCallback(
		({ limitType }: { limitType: string }) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'collabLimitReached',
					actionSubject: 'editor',
					actionSubjectId: contentId,
					source: 'nativeEditor',
					objectId: contentId,
					containerId: spaceId,
					attributes: {
						isLivePage,
						limitType,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, contentId, spaceId, isLivePage],
	);

	useEffect(() => {
		if (isEditLimitExceeded && !hasShownEditLimitFlag.current) {
			sendLimitReachedEvent({ limitType: 'EDIT' });
		}

		if (isEditLimitExceeded && !hasShownEditLimitFlag.current) {
			// set the ref to true and display the error message
			// to notify the user they cannot edit
			hasShownEditLimitFlag.current = true;
			void flags.showErrorFlag({
				title: <FormattedMessage {...i18n.collabLimitReachedTitle} />,
				description: <FormattedMessage {...i18n.collabLimitReachedDescription} />,
			});
		} else if (hasShownEditLimitFlag.current) {
			// reset the ref to false and show the success message
			// to notify the user they can edit again
			hasShownEditLimitFlag.current = false;
			void flags.showSuccessFlag({
				title: <FormattedMessage {...i18n.underCollabLimitTitle} />,
				description: <FormattedMessage {...i18n.underCollabLimitDescription} />,
			});
		}
	}, [isEditLimitExceeded, flags, sendLimitReachedEvent, isLiveViewLimitExceeded]);

	useEffect(() => {
		if (!isLivePage) {
			return;
		}

		if (isLiveViewLimitExceeded && !hasShownLiveViewLimitFlag.current) {
			sendLimitReachedEvent({ limitType: 'VIEW' });
		}

		if (isLiveViewLimitExceeded) {
			collabEditProvider?.destroy();

			hasShownLiveViewLimitFlag.current = true;
			void flags.showErrorFlag({
				title: <FormattedMessage {...i18n.livePagesViewLimitReachedTitle} />,
				description: <FormattedMessage {...i18n.livePagesViewLimitReachedDescription} />,
			});
		}
	}, [isLiveViewLimitExceeded, flags, isLivePage, collabEditProvider, sendLimitReachedEvent]);
};
